import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';

interface InterviewResultsMetrics {
  totalCandidatesInterviewed?: number;
  interviewCompletionRate?: number;
  averageInterviewScore?: number;
  strongFitCandidates?: number;
}

interface InterviewResultItem {
  rank?: number;
  sessionId?: string;
  profileId?: string;
  candidateName?: string;
  overallScore?: number;
  aiRecommend?: string;
  dateCompleted?: string;
}

interface InterviewResultsPage {
  totalElements?: number;
  totalPages?: number;
  size?: number;
  content?: InterviewResultItem[];
  number?: number;
  numberOfElements?: number;
  first?: boolean;
  last?: boolean;
  empty?: boolean;
}

interface InterviewStatisticsResponse {
  metrics?: InterviewResultsMetrics;
  results?: InterviewResultsPage;
}

@Component({
  selector: 'app-interview-results',
  templateUrl: './interview-results.component.html',
  styleUrls: ['./interview-results.component.css']
})
export class InterviewResultsComponent implements OnInit {
  private readonly baseUrl = environment.apiUrl;

  interviewId = '';
  isLoading = false;

  keyMetrics = {
    totalCandidates: 0,
    completionRate: 0,
    averageScore: 0,
    strongFitCandidates: 0
  };

  resultsList: InterviewResultItem[] = [];
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.interviewId = params.get('id') || '';
      this.loadResults();
    });
  }

  loadResults(): void {
    if (!this.interviewId) {
      this.resultsList = [];
      return;
    }

    this.isLoading = true;
    const params = new HttpParams()
      .set('page', this.currentPage.toString())
      .set('size', this.pageSize.toString());

    this.http
      .get<InterviewStatisticsResponse>(
        `${this.baseUrl}api/b2b/interview/company/interview/${this.interviewId}/statistics`,
        { params }
      )
      .subscribe({
        next: (response) => {
          const metrics = response.metrics ?? {};
          const results = response.results ?? {};

          this.keyMetrics = {
            totalCandidates: metrics.totalCandidatesInterviewed ?? 0,
            completionRate: metrics.interviewCompletionRate ?? 0,
            averageScore: metrics.averageInterviewScore ?? 0,
            strongFitCandidates: metrics.strongFitCandidates ?? 0
          };

          this.resultsList = (results.content ?? []).map((item) => ({
            rank: item.rank,
            sessionId: item.sessionId,
            profileId: item.profileId,
            candidateName: item.candidateName ?? '-',
            overallScore: item.overallScore,
            aiRecommend: item.aiRecommend ?? '-',
            dateCompleted: item.dateCompleted ?? '-'
          }));

          this.totalElements = results.totalElements ?? 0;
          this.totalPages = results.totalPages ?? 0;
          this.currentPage = results.number ?? this.currentPage;
          this.pageSize = results.size ?? this.pageSize;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load interview statistics:', error);
          this.resultsList = [];
          this.isLoading = false;
        }
      });
  }

  goBack(): void {
    this.router.navigate(['/company-admin/virtual-interviews']);
  }

  viewRanking(result: InterviewResultItem): void {
    const targetId = result.sessionId || result.profileId || this.interviewId;
    this.router.navigate(['/company-admin/interview-result', targetId]);
  }

  getRecommendationClass(recommend: string | undefined): string {
    switch ((recommend || '').toUpperCase()) {
      case 'STRONG FIT':
        return 'strong-fit';
      case 'GOOD FIT':
        return 'good-fit';
      case 'POTENTIAL FIT':
        return 'potential-fit';
      case 'REJECT':
        return 'reject-fit';
      default:
        return '';
    }
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }

  formatScore(score: number | undefined): string {
    return typeof score === 'number' ? score.toFixed(1) : '-';
  }

  formatDate(date: string | undefined): string {
    if (!date) return '-';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime())
      ? '-'
      : parsed.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
  }

  get activePage(): number {
    return this.currentPage + 1;
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const cur = this.activePage;
    const pages: number[] = [1];
    if (cur > 3) pages.push(-1);
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page - 1;
    this.loadResults();
  }

  get firstItem(): number {
    return this.totalElements === 0 ? 0 : this.currentPage * this.pageSize + 1;
  }

  get lastItem(): number {
    return Math.min(this.activePage * this.pageSize, this.totalElements);
  }
}
